import _ from "lodash";
import { Input, message } from "antd";
import { useMutation } from "@apollo/client";
import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";

import { useAuthStore } from "../store";
import { UserException } from "../utils/helpers";
import { parseError } from "../utils/parseError";
import { OTPView } from "../components/secondary";
import AuthForm from "../components/forms/AuthForm";
import AuthPageShell from "../components/forms/AuthPageShell";
import BlueButton from "../components/buttons/BlueButton";
import { CelluarInput, CheckBox } from "../components/inputs";
import { BodyOne, HeadingOne } from "../components/typography";
import { CONFIRM_SIGN_UP, SIGN_UP } from "../graphql/Mutations";
import InputValidator from "../components/inputs/InputValidator";
import { validators } from "../constants";
import { useVerificationOTP } from "../hooks";
import SupportBlock from "../components/info/SupportBlock";
import useInviteMutation from "../hooks/useInviteMutation";
import SimpleLoader from "../components/simpleLoader";
import bugfenderHelper from "../services/bugfender";
import mixpanelService from "../services/mixpanel";

/**
 * Invited-member signup: create account then confirm email OTP.
 */
const InviteMemberForm = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const inviteDetail = useAuthStore((s) => s.inviteDetail);
  const setInviteDetail = useAuthStore((s) => s.setInviteDetail);
  const signUpFormInputs = useAuthStore((s) => s.signUpInputs);
  const setSignUpFormInputs = useAuthStore((s) => s.setSignUpInputs);

  const { handleSubmit, formState: { errors }, control, getValues } = useForm();
  const [accountCreationLevel, setAccountCreationLevel] = useState(1);

  const [
    signUpMutation,
    { loading: signUpLoading, data: signUpData, error: signUpError },
  ] = useMutation(SIGN_UP);

  const [
    confirmSignUpMutation,
    {
      loading: confirmSignUpLoading,
      data: confirmSignUpData,
      error: confirmSignUpError,
    },
  ] = useMutation(CONFIRM_SIGN_UP);

  const { OTP: confirmSignUpOTP, handleSetOTP: handleConfirmSignUpSetOTP } =
    useVerificationOTP();

  const { loading: loginLoading, handleInviteLogin } = useInviteMutation({
    variable: {
      username: signUpFormInputs.email,
      password: signUpFormInputs.password,
    },
    onCompletedCallback: () => {
      setAccountCreationLevel(3);
    },
  });

  const genders = [
    { value: "Male", label: "Male", id: 1 },
    { value: "Female", label: "Female", id: 2 },
    { value: "None", label: "Prefer not to say", id: 3 },
  ];

  const onSubmit = async (data) => {
    try {
      const input = {
        email: data.email,
        gender: data.gender,
        firstName: data.name,
        lastName: data.family_name,
        password: data.password,
        phoneNumber: `${data.phone_number}`,
      };

      signUpMutation({
        variables: { input },
      });
    } catch (err) {
      console.error("InviteMemberForm | onSubmit [ERROR]\n", err);
    }
  };

  const handleConfirmSignUpOTP = useCallback(() => {
    const formEmail = signUpFormInputs?.email;

    if (!formEmail) {
      bugfenderHelper.error("Form Email is undefined");
      throw UserException("handleConfirmSignUpOTP - Form Email is undefined");
    }

    confirmSignUpMutation({
      variables: {
        code: confirmSignUpOTP,
        email: formEmail,
      },
    });
  }, [confirmSignUpOTP, signUpFormInputs?.email]);

  useEffect(() => {
    const hasInviteDetail = _.has(state, "invite.inviteResult");

    if (!hasInviteDetail) return;

    setInviteDetail(state.invite);

    mixpanelService.track("App.Invite.Member", { inviteData: state.invite });
  }, [state]);

  useEffect(() => {
    if (!signUpData) return;

    if (signUpData?.signUp?.Error) {
      message.error(
        typeof signUpData.signUp.Error.message === "string"
          ? signUpData.signUp.Error.message
          : "An error occurred during registration."
      );
      return;
    }

    const processSignUp = async () => {
      try {
        const formValues = getValues();

        const signUpInput = {
          email: formValues.email,
          gender: formValues.gender,
          firstName: formValues.name,
          lastName: formValues.family_name,
          password: formValues.password,
          phoneNumber: formValues.phone_number,
        };

        if (signUpData?.signUp) {
          message.success(
            typeof signUpData.signUp.text === "string"
              ? signUpData.signUp.text
              : "Account created successfully"
          );

          setSignUpFormInputs({
            ...signUpInput,
            username: signUpData.signUp.username,
          });

          mixpanelService.people.set({
            email: formValues.email,
            gender: formValues.gender,
            firstName: formValues.name,
            lastName: formValues.family_name,
          });

          mixpanelService.track("App.Sign.Up");

          setAccountCreationLevel(2);
          return;
        }

        throw null;
      } catch (err) {
        bugfenderHelper.error(err);
        console.error("signUpMutation [ERROR]\n", err);
        message.error("We've encountered an issue with your registration.");
      }
    };

    processSignUp();
  }, [signUpData]);

  useEffect(() => {
    if (!signUpError) return;

    const processSignUpError = async () => {
      try {
        const errorMessage = parseError(signUpError);
        message.error(errorMessage);
        bugfenderHelper.error(errorMessage);
      } catch (err) {
        bugfenderHelper.error(err);
        console.error("signUpMutation [ERROR]\n", err);
        message.error("We've encountered an issue with your registration.");
      }
    };

    processSignUpError();
  }, [signUpError]);

  useEffect(() => {
    if (!confirmSignUpData) return;

    if (confirmSignUpData?.confirmSignUp?.Error) {
      message.error(
        typeof confirmSignUpData.confirmSignUp.Error.message === "string"
          ? confirmSignUpData.confirmSignUp.Error.message
          : "An error occurred during verification."
      );
      return;
    }

    const processConfirmSignUp = async () => {
      try {
        if (confirmSignUpData?.confirmSignUp !== null) {
          message.success("Verification successful");
          handleInviteLogin();
          return;
        }

        throw null;
      } catch (err) {
        message.error("We've encountered an issue with your registration.");
        console.error("confirmSignUpMutation [Success]\n", err);
      }
    };

    processConfirmSignUp();
  }, [confirmSignUpData]);

  useEffect(() => {
    if (!confirmSignUpError) return;

    const processConfirmSignUpError = async () => {
      try {
        console.error("CONFIRM-SIGN-UP-ERROR:\n", confirmSignUpError);
        const errorMessage = parseError(confirmSignUpError);
        message.error(errorMessage);
        bugfenderHelper.error(errorMessage);
      } catch (err) {
        bugfenderHelper.error(err);
        message.error("We've encountered an issue with your registration.");
        console.error(
          UserException(
            "confirmSignUpMutation [Failure] - Failed to confirm user sign-up"
          )
        );
      }
    };

    processConfirmSignUpError();
  }, [confirmSignUpError]);

  if (!inviteDetail || loginLoading) {
    return (
      <AuthPageShell>
        <AuthForm>
          <SimpleLoader />
        </AuthForm>
      </AuthPageShell>
    );
  }

  if (accountCreationLevel === 2) {
    return (
      <AuthPageShell>
        <AuthForm>
          <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
            Verification step
          </HeadingOne>
          <OTPView
            isSignUpOTP={true}
            disabled={confirmSignUpLoading}
            OTP={confirmSignUpOTP}
            verificationCodeDestination={signUpFormInputs.email}
            handleSetOTP={handleConfirmSignUpSetOTP}
            handleOTP={handleConfirmSignUpOTP}
          />
          <BlueButton
            className="w-full mt-4 py-2.5 font-semibold hover:text-white"
            onClick={handleConfirmSignUpOTP}
            isLoading={
              confirmSignUpLoading === false && loginLoading === false
                ? false
                : true
            }
            disabled={confirmSignUpLoading || confirmSignUpOTP.length !== 6}
          >
            Continue
          </BlueButton>
          <div className="auth-footer-rule" />
          <SupportBlock area="sign-up-index-step-2" tailwindProps="mt-0" />
        </AuthForm>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AuthForm>
          <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
            Create Account
          </HeadingOne>
          <div className="flex flex-col">
            <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>
              Name
            </BodyOne>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <Input
                  size="large"
                  placeholder="Enter your first name here"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
            {errors.name && (
              <BodyOne className="text-red-600">
                Please enter your name
              </BodyOne>
            )}
          </div>
          <div className="flex flex-col mt-4">
            <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>
              Last Name
            </BodyOne>
            <Controller
              name="family_name"
              control={control}
              defaultValue=""
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <Input
                  size="large"
                  placeholder="Enter your last name here"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
            {errors.family_name && (
              <BodyOne className="text-red-600">
                Please enter your last name
              </BodyOne>
            )}
          </div>
          <div className="flex flex-col mt-4">
            <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>
              Email address
            </BodyOne>
            <Controller
              name="email"
              control={control}
              defaultValue={inviteDetail?.email ? inviteDetail.email : ""}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <Input
                  size="large"
                  placeholder="Enter your email address here"
                  onChange={onChange}
                  value={value}
                  disabled={inviteDetail ? true : false}
                />
              )}
            />
            {errors.email && (
              <BodyOne className="text-red-600">
                Please enter your email
              </BodyOne>
            )}
          </div>
          <div className="flex flex-col mt-4">
            <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>
              Password
            </BodyOne>
            <Controller
              name="password"
              control={control}
              defaultValue=""
              rules={{
                required: true,
                validate: (value) => validators.isPasswordValid(value),
              }}
              render={({ field: { onChange, value } }) => (
                <InputValidator
                  validatorStates={validators.getPasswordValidatorStates(
                    value
                  )}
                  iconTitles={["8+ Letters", "1 Uppercase", "1 Number"]}
                >
                  <Input.Password
                    style={{ border: "1px solid transparent" }}
                    size="large"
                    placeholder="Enter your password here"
                    onChange={onChange}
                    value={value}
                  />
                </InputValidator>
              )}
            />
            {errors.password && (
              <BodyOne className="text-red-600">
                {errors.password.message}
              </BodyOne>
            )}
          </div>
          <div className="flex flex-col mt-4">
            <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>
              Mobile number
            </BodyOne>
            <Controller
              name="phone_number"
              control={control}
              defaultValue=""
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <CelluarInput onChange={onChange} value={value} />
              )}
            />
            {errors.phone_number && (
              <BodyOne className="text-red-600">
                Please enter your mobile number
              </BodyOne>
            )}
          </div>
          <div className="flex flex-col mt-4">
            <BodyOne className="mb-1.5 text-slate-600 font-medium">Gender</BodyOne>
            <div className="flex justify-between flex-wrap gap-2">
              <Controller
                name="gender"
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <>
                    {genders.map((gender) => (
                      <div key={gender.id} className="flex flex-row">
                        <CheckBox
                          className="mt-1 mr-2"
                          name={gender.value}
                          onChange={() => onChange(gender.value)}
                          value={value}
                        />
                        <span>{gender.label}</span>
                      </div>
                    ))}
                  </>
                )}
              />
            </div>
          </div>
          <BlueButton
            className="w-full mt-6 py-2.5 font-semibold hover:text-white"
            type="submit"
            isLoading={signUpLoading}
          >
            Proceed
          </BlueButton>
          <p className="text-center mt-5 mb-0 text-xs leading-5 text-slate-500 font-poppins">
            By continuing you agree to our{" "}
            <Link
              className="text-[#0458f6] font-medium hover:underline"
              to="/legal/terms-of-use"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              className="text-[#0458f6] font-medium hover:underline"
              to="/legal/privacy-policy"
            >
              Privacy Policy
            </Link>
          </p>
          <p className="text-center mt-3 mb-0 text-xs leading-5 text-slate-500 font-poppins">
            Already have an account?{" "}
            <span
              className="text-[#0458f6] font-medium cursor-pointer hover:underline"
              onClick={() =>
                navigate(
                  "/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM="
                )
              }
            >
              Login
            </span>
          </p>
          <div className="auth-footer-rule" />
          <SupportBlock area="sign-up-index-step-1" tailwindProps="mt-0" />
        </AuthForm>
      </form>
    </AuthPageShell>
  );
};

export default InviteMemberForm;
